import React, { memo, useCallback, useMemo } from 'react'
import { GridColumnVisibilityModel } from '@mui/x-data-grid-pro'
import { useSelector } from 'react-redux'
import { EmptyMarkets } from 'src/components/market/grid/EmptyMarkets'
import { GridTable } from 'src/components/shared/grid/GridTable'
import {
  MARKETS_GRID_COLUMNS_STORAGE_KEY,
  marketPageSizeOptions,
} from 'src/constants/market/constants'
import { MarketsTableFields } from 'src/constants/market/enums'
import { buildMarketColumns } from 'src/constants/market/marketGrid'
import type { Market } from 'src/interfaces/market'
import { getMarkets, getMarketsLoading } from 'src/selectors/market'
import { filterMarkets } from 'src/utils/marketList'
import { useGridColumns } from 'src/utils/hooks/grid/useGridColumns'
import { useGridPagination } from 'src/utils/hooks/grid/useGridPagination'

interface Props {
  searchQuery: string
  canManage: boolean
  onEditMarket(market: Market): void
}

const getRowId = (row: Market) => {
  return row.id
}

// Name identifies the row and the actions column has no meaning when hidden; both also carry
// `hideable: false`, which is what removes them from the manage-columns panel. This guard
// additionally stops a programmatic visibility-model change from persisting a hide.
const UNHIDEABLE_COLUMNS: Array<string> = [MarketsTableFields.title, MarketsTableFields.actions]

const MarketsTableComponent = ({ searchQuery, canManage, onEditMarket }: Props) => {
  const markets = useSelector(getMarkets)
  const isLoading = useSelector(getMarketsLoading)

  const { paginationModel, setPaginationModel } = useGridPagination(marketPageSizeOptions)

  // A fresh array each render would reset the grid's column state, so the factory call is memoized.
  const marketColumns = useMemo(() => {
    return buildMarketColumns(onEditMarket, canManage)
  }, [onEditMarket, canManage])

  const { includedColumns, handleAddExcludedColumn, handleRemoveExcludedColumn } = useGridColumns(
    marketColumns,
    MARKETS_GRID_COLUMNS_STORAGE_KEY,
  )

  // The client holds every market (the endpoint takes no search param), so filtering is here.
  const visibleMarkets = useMemo(() => {
    return filterMarkets(markets, searchQuery)
  }, [markets, searchQuery])

  const columnVisibilityModel = useMemo(() => {
    return marketColumns.reduce<GridColumnVisibilityModel>((accumulator, column) => {
      accumulator[column.field] = includedColumns.includes(column.field)

      return accumulator
    }, {})
  }, [marketColumns, includedColumns])

  // `useGridColumns` stores excluded fields as a flat list; the grid speaks a visibility model.
  // Translate both ways so menu changes persist to localStorage across reloads.
  const handleColumnVisibilityModelChange = useCallback(
    (model: GridColumnVisibilityModel) => {
      marketColumns.forEach((column) => {
        if (UNHIDEABLE_COLUMNS.includes(column.field)) {
          return
        }

        if (model[column.field] === false) {
          handleAddExcludedColumn(column.field)

          return
        }

        handleRemoveExcludedColumn(column.field)
      })
    },
    [marketColumns, handleAddExcludedColumn, handleRemoveExcludedColumn],
  )

  const hasSearchQuery = searchQuery.trim() !== ''

  // `GridTable` renders EmptyElement with no props, so the search-aware copy is bound here.
  const EmptyElement = useMemo(() => {
    const BoundEmptyMarkets = () => {
      return <EmptyMarkets hasSearchQuery={hasSearchQuery} />
    }

    return BoundEmptyMarkets
  }, [hasSearchQuery])

  // Pinning is intentionally left uncontrolled, for the reason documented on CustomersTable:
  // `useGridColumns`'s persisted list has no left/right distinction.
  return (
    <GridTable<Market>
      rows={visibleMarkets}
      columns={marketColumns}
      getRowId={getRowId}
      isLoading={isLoading}
      sortingMode="client"
      paginationMode="client"
      paginationModel={paginationModel}
      onPaginationModelChange={setPaginationModel}
      pageSizeOptions={marketPageSizeOptions}
      initialState={{
        // Matches the backend's own `title ASC` ordering so the first paint does not reshuffle.
        sorting: { sortModel: [{ field: MarketsTableFields.title, sort: 'asc' }] },
      }}
      columnVisibilityModel={columnVisibilityModel}
      onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
      EmptyElement={EmptyElement}
      tableDataTestId="markets-list-table"
      rowDataTestIdPrefix="markets-list"
    />
  )
}

export const MarketsTable = memo(MarketsTableComponent)
